// file: frontend/src/hooks/useLogsData.js
import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { fetchRecentLogs, searchLogsThunk, createLog, clearLogs } from "../slices/logSlice";

/**
 * Custom hook for accessing and managing logs data in Redux.
 * Matches RAVEN standard hook structure.
 */
export function useLogsData() {
	const dispatch = useDispatch();

	// --- Redux state ---
	const items = useSelector((state) => state.logs.items);
	const meta = useSelector((state) => state.logs.meta);
	const status = useSelector((state) => state.logs.status);
	const error = useSelector((state) => state.logs.error);

	// --- Actions ---
	const loadLogs = useCallback(
		(params) => {
			// Generic log reads use GET /api/log.
			return dispatch(fetchRecentLogs(params));
		},
		[dispatch]
	);

	const searchLogs = useCallback(
		(params) => {
			// Expose the simple hook-facing name while dispatching the explicitly named thunk.
			// Advanced log search uses POST /api/log/search. Keep it distinct from generic GET reads so
			// future consumers can still choose the GET path intentionally.
			return dispatch(searchLogsThunk(params));
		},
		[dispatch]
	);

	const addLog = useCallback(
		(log) => {
			return dispatch(createLog({ log }));
		},
		[dispatch]
	);

	const resetLogs = useCallback(() => {
		dispatch(clearLogs());
	}, [dispatch]);

	return {
		items,
		meta,
		status,
		error,
		loadLogs,
		searchLogs,
		addLog,
		resetLogs,
	};
}

export default useLogsData;

// Usage example
// import React, { useEffect } from "react";
// import { useLogsData } from "../hooks/useLogsData";

// export default function LogsList() {
// 	const { items, status, error, loadLogs } = useLogsData();

// 	useEffect(() => {
// 		loadLogs({ limit: 50 }); // fetch last 50 logs
// 	}, [loadLogs]);

// 	if (status.fetchRecent === "loading") return <div>Loading...</div>;
// 	if (error.fetchRecent) return <div>Error: {error.fetchRecent}</div>;

// 	return (
// 		<ul>
// 			{items.map((log) => (
// 				<li key={log.id}>{log.message}</li>
// 			))}
// 		</ul>
// 	);
// }
